'use client';

import React, { useEffect, useState } from 'react';
import { Button } from '@/app/_module/components/ui/button';
import { Input } from '@/app/_module/components/ui/input';
import { useToast } from '@/app/_module/hooks/use-toast';
import { cn } from '@/app/_module/lib/utils';
import { a2pService, type CallerIdState } from '@/app/_module/services/a2pService';
import { Notice, Panel, Section, StatusLabel, controlBorder, focusRing } from './overview/primitives';

const IN_REVIEW = ['draft', 'pending-review', 'in-review'];

/** Same rule the backend enforces: ≤15, starts with a letter, letters/digits/periods/commas/spaces. */
function nameProblem(name: string): string | null {
  if (!name) return 'Enter a name.';
  if (name.length > 15) return 'Use 15 characters or fewer.';
  if (!/^[A-Z]/.test(name)) return 'Start with a letter.';
  if (!/^[A-Z0-9., ]+$/.test(name)) return 'Use only letters, numbers, periods, commas and spaces.';
  return null;
}

/**
 * Caller ID name for landline calls, one per business registration. The owner
 * (or the agency's owner/admin for an agency registration) sets it; agents
 * covered by it see it read-only. Hidden for sole props, who can't have one.
 */
export default function CallerIdNameCard() {
  const { toast } = useToast();
  const [state, setState] = useState<CallerIdState | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    a2pService
      .getCallerId()
      .then((s) => {
        setState(s);
        setName(s.status === 'twilio-rejected' ? s.displayName || '' : s.displayName || s.suggestedName || '');
      })
      .catch(() => setState(null));
  }, []);

  if (!state?.eligible) return null;

  const status = state.status;
  const approved = status === 'twilio-approved';
  const inReview = !!status && IN_REVIEW.includes(status);
  const rejected = status === 'twilio-rejected';
  const cleaned = name.toUpperCase().replace(/\s+/g, ' ').trim();
  const problem = nameProblem(cleaned);
  const canEdit = state.canManage && !approved && !inReview;
  const business = state.registrationName || 'your registered business';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (problem) return;
    setSaving(true);
    try {
      const next = await a2pService.setCallerId(cleaned);
      setState(next);
      toast({ title: 'Caller ID name submitted', description: `${cleaned} is in review. This usually takes a few days.` });
    } catch (error: any) {
      toast({ title: "Couldn't submit", description: error?.message || 'Try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const badge = approved ? (
    <StatusLabel tone="ok">Approved</StatusLabel>
  ) : inReview ? (
    <StatusLabel tone="waiting">In review</StatusLabel>
  ) : rejected ? (
    <StatusLabel tone="blocked">Not approved</StatusLabel>
  ) : null;

  return (
    <Section
      id="a2p-caller-id"
      title="Caller ID name"
      description="Shows your business name when you call a landline."
      aside={badge}
    >
      <Panel className="px-4 py-4 text-sm leading-relaxed text-neutral-600 sm:px-5 sm:py-5">
        {approved && (
          <p>
            Landlines see <span className="font-medium tracking-wide text-neutral-900">{state.displayName}</span>
            {state.viaAgency && !state.canManage ? `, set by ${state.registrationName}` : ''}. Carriers can take a few
            days to start showing it.{state.canManage ? ' To change it, email support.' : ''}
          </p>
        )}
        {inReview && (
          <p>
            <span className="font-medium tracking-wide text-neutral-900">{state.displayName}</span> is in review. This
            usually takes a few days.
          </p>
        )}
        {!state.canManage && !approved && !inReview && (
          <p>
            {state.viaAgency
              ? `${state.registrationName || 'Your agency'} hasn't set a caller ID name yet.`
              : 'No caller ID name set yet.'}
          </p>
        )}
        {canEdit && (
          <form onSubmit={submit} className="space-y-4">
            {rejected && (
              <Notice tone="blocked">That name wasn&apos;t approved. Use a name that matches {business}.</Notice>
            )}
            <div>
              <label htmlFor="a2p-caller-id-name" className="font-medium text-neutral-900">
                Name shown on calls
              </label>
              <div className="mt-1.5 flex flex-col gap-2.5 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1">
                  <Input
                    id="a2p-caller-id-name"
                    value={name}
                    onChange={(e) => setName(e.target.value.toUpperCase())}
                    maxLength={15}
                    placeholder={state.suggestedName || 'YOUR BUSINESS'}
                    aria-describedby="a2p-caller-id-hint"
                    aria-invalid={!!problem && !!cleaned}
                    className={cn('h-11 uppercase tracking-wide', controlBorder, focusRing)}
                  />
                  <p
                    id="a2p-caller-id-hint"
                    className={cn('mt-1.5 text-xs', problem && cleaned ? 'text-red-700' : 'text-neutral-500')}
                  >
                    {problem && cleaned ? (
                      problem
                    ) : (
                      <>
                        <span className="tabular-nums">{cleaned.length}/15</span>. Must match {business}.
                      </>
                    )}
                  </p>
                </div>
                <Button type="submit" disabled={!!problem || saving} className={cn('h-11 shrink-0 px-5', focusRing)}>
                  {saving ? 'Submitting…' : 'Submit for review'}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Panel>
    </Section>
  );
}
